"use client";

import { useEffect, useState } from "react";
import { Alert, Snackbar } from "@mui/material";
import { useAppTranslate } from "src/hooks/useAppTranslate";
import { captureCreditFromUrl, CreditArrival } from "src/utils/credit-token";
import { useQuery } from "@tanstack/react-query";
import { claimProblemMessage } from "src/utils/credit-problem";
import { useOrder } from "@contexts/index";
import { fetchOrder } from "src/api/order.api";
import { orderQueryKey } from "src/hooks/useOrder";
import { hasCreditApplied } from "src/utils/credit-amount";
import { creditEditable } from "src/utils/credit-offer";

/**
 * The first thing that happens when someone clicks the button in their
 * "we've added €10 to your account" email.
 *
 * The claim endpoint verified the emailed token, exchanged it for one that
 * lives two hours, and redirected here with `?credit_token=` — or, when
 * something was wrong, `?credit_error=<reason>`. Until this existed the shop
 * ignored both and the customer landed on the homepage with nothing happening
 * at all, which is indistinguishable from a broken link.
 *
 * Mounted once per locale layout, so it catches the landing whichever page the
 * redirect chose. It runs on mount only: the params exist on exactly one
 * navigation and are stripped from the URL the moment they are read, so
 * re-running on every route change would find nothing.
 *
 * A failed claim is not an error page. The person did nothing wrong, cannot
 * retry, and most often simply left the link sitting for more than two hours —
 * so they get a sentence that says what happened and what to do, and the shop
 * carries on underneath it.
 */
export function CreditClaimBanner() {
  const t = useAppTranslate();
  const [arrival, setArrival] = useState<CreditArrival | null>(null);

  useEffect(() => {
    const result = captureCreditFromUrl();
    if (result.token || result.problem) setArrival(result);
  }, []);

  // A `spent` claim may only mean the credit sits on this browser's own cart
  // (the email link clicked a second time, ops walk B4). The cart is read
  // only then, from the cache the checkout already fills, and never
  // refetched on every page the layout renders.
  const { mainOrderId, guestToken } = useOrder();
  const askCart = arrival?.problem === "spent" && !!mainOrderId && !!guestToken;
  const cartQuery = useQuery({
    queryKey: orderQueryKey(mainOrderId ?? ""),
    queryFn: () => fetchOrder(mainOrderId as string, guestToken as string),
    enabled: askCart,
    staleTime: 10 * 1000,
  });

  if (!arrival) return null;
  // Wait for the cart before choosing between "used" and "on your cart".
  if (askCart && cartQuery.isPending) return null;

  const cart = askCart ? cartQuery.data : undefined;
  const problem = arrival.problem;
  const message = problem
    ? (() => {
        const m = claimProblemMessage(problem, {
          cartHoldsCredit: creditEditable(cart) && hasCreditApplied(cart),
        });
        return t(m.key, m.fallback);
      })()
    : t(
        "credit.ready",
        "Vaše dobroimetje je pripravljeno. Uporabili ga boste lahko pri zaključku naročila.",
      );

  return (
    <Snackbar
      open
      // Long enough to read a sentence about money in a second language, and
      // dismissible — but it does not block the page either way, because the
      // customer's next move is to shop.
      autoHideDuration={problem ? 15000 : 8000}
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
      onClose={(_, reason) => {
        if (reason === "clickaway") return;
        setArrival(null);
      }}
    >
      <Alert
        severity={problem ? "warning" : "success"}
        variant="filled"
        onClose={() => setArrival(null)}
        sx={{ maxWidth: 520 }}
      >
        {message}
      </Alert>
    </Snackbar>
  );
}
